import { type ParsedUrlQuery } from "querystring"
import { type WizardSteps } from "@krakentech/blueprint-onboarding/next-wizard/pages-router"
import { DirectAffiliateCodes } from "@/constants/affiliateCode"
import { EnergyType, isDualEnergy, isGasOnly } from "@/features/subscription"
import { isMonoEnergy } from "@/features/subscription/utils/isMonoEnergy"
import { EnergyTypeRoutes, OnboardingRouteNames, onboardingRoutesPaths } from "@/routes/onboarding"
import { nestedQueryParams } from "@/utils/nestedQueryParams"
import { type OnboardingFormValues } from "../types/onboardingFormValues"

const ENERGY_TYPE_ROUTES_MAP = {
    [EnergyType.Electricity]: EnergyTypeRoutes.Electricity,
    [EnergyType.Gas]: EnergyTypeRoutes.Gas,
}

function getOnboardingQueryParams(formValues: OnboardingFormValues) {
    const queryParams: ParsedUrlQuery = {}

    if (isGasOnly(formValues.selectedEnergyTypes)) {
        queryParams.produit = "gaz"
    } else if (isDualEnergy(formValues.selectedEnergyTypes)) {
        queryParams.produit = "dual"
    }

    if (formValues.affiliateCode && !DirectAffiliateCodes.includes(formValues.affiliateCode)) {
        queryParams.affiliation = formValues.affiliateCode
    }

    if (formValues.affiliateQueryParams) {
        queryParams.affiliateQueryParams = nestedQueryParams.stringify(
            formValues.affiliateQueryParams
        )
    }

    if (formValues.electricity.supplyPoint.meterPoint.id) {
        queryParams.electricityMeterPointId = formValues.electricity.supplyPoint.meterPoint.id
    }

    if (formValues.gas.supplyPoint.meterPoint.id) {
        queryParams.gasMeterPointId = formValues.gas.supplyPoint.meterPoint.id
    }

    if (formValues.propertyId) {
        queryParams.propertyId = formValues.propertyId
    }

    return queryParams
}

function getCommonPostQuoteSteps(formValues: OnboardingFormValues) {
    const query = getOnboardingQueryParams(formValues)
    const commonSteps = [
        {
            name: OnboardingRouteNames.Quote,
            pathname: onboardingRoutesPaths.quote.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.MeterReadings,
            pathname: onboardingRoutesPaths.meterReadings.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PersonalDetails,
            pathname: onboardingRoutesPaths.personalDetails.resolve(),
            query,
        },
    ]

    const postCheckoutSteps = [
        {
            name: OnboardingRouteNames.PaymentDetailsRetry,
            pathname: onboardingRoutesPaths.paymentDetailsRetry.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PaymentDetailsErrorIban,
            pathname: onboardingRoutesPaths.paymentDetailsErrorIban.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PaymentDetailsErrorSignature,
            pathname: onboardingRoutesPaths.paymentDetailsErrorSignature.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PaymentDetailsErrorPrepayment,
            pathname: onboardingRoutesPaths.paymentDetailsErrorPrepayment.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PaymentDetailsPendingPrepayment,
            pathname: onboardingRoutesPaths.paymentDetailsPendingPrepayment.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PaymentDetailsUnpaidBills,
            pathname: onboardingRoutesPaths.paymentDetailsUnpaidBills.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.PrePaymentCdf,
            pathname: onboardingRoutesPaths.prePaymentCdf.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.SecurityDeposit,
            pathname: onboardingRoutesPaths.securityDeposit.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.Onboarded,
            pathname: onboardingRoutesPaths.onboarded.resolve(),
            query,
        },
    ]

    return [
        ...commonSteps,
        ...[
            {
                name: OnboardingRouteNames.PaymentDetails,
                pathname: onboardingRoutesPaths.paymentDetails.resolve(),
                query,
            },
            {
                name: OnboardingRouteNames.PaymentDetailsFinancialSituation,
                pathname: onboardingRoutesPaths.paymentDetailsFinancialSituation.resolve(),
                query,
            },
            {
                name: OnboardingRouteNames.OrderReview,
                pathname: onboardingRoutesPaths.orderReview.resolve(),
                query,
            },
            {
                name: OnboardingRouteNames.PrePayment,
                pathname: onboardingRoutesPaths.prePayment.resolve(),
                query,
            },
        ],
        ...postCheckoutSteps,
    ] as const satisfies WizardSteps<OnboardingRouteNames>
}

export function getDirectSteps(formValues: OnboardingFormValues) {
    const query = getOnboardingQueryParams(formValues)

    const isMonoLoggedEnergy =
        !!formValues.propertyId &&
        !!formValues.propertyAddress &&
        isMonoEnergy(formValues.selectedEnergyTypes)

    const commonSteps = getCommonPostQuoteSteps(formValues)

    const steps = [
        {
            name: OnboardingRouteNames.MeterDetails,
            pathname: isMonoLoggedEnergy
                ? onboardingRoutesPaths.meterDetailsMonoEnergy.resolve({
                      pathParams: {
                          energyType: ENERGY_TYPE_ROUTES_MAP[formValues.selectedEnergyTypes[0]],
                      },
                  })
                : onboardingRoutesPaths.meterDetails.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.ProviderCalendar,
            pathname: onboardingRoutesPaths.providerCalendar.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.EnergyLandscape,
            pathname: onboardingRoutesPaths.energyLandscape.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.ElectricityMeterIneligible,
            pathname: onboardingRoutesPaths.electricityMeterIneligible.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.GasMeterIneligible,
            pathname: onboardingRoutesPaths.gasMeterIneligible.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.AddressIneligible,
            pathname: onboardingRoutesPaths.addressIneligible.resolve(),
            query,
        },
        ...commonSteps,
    ] as const satisfies WizardSteps<OnboardingRouteNames>

    return steps
}

export function getSentQuoteDirectSteps(formValues: OnboardingFormValues) {
    return getCommonPostQuoteSteps(formValues)
}

export function getMoveOutSteps(formValues: OnboardingFormValues) {
    const commonSteps = getCommonPostQuoteSteps(formValues)
    const query = getOnboardingQueryParams(formValues)

    return [
        {
            name: OnboardingRouteNames.MoveOut,
            pathname: onboardingRoutesPaths.moveOut.resolve({}),
            query: { accountNumber: formValues.accountNumber ?? undefined },
        },
        {
            name: OnboardingRouteNames.MoveOutIneligible,
            pathname: onboardingRoutesPaths.moveOutIneligible.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.MoveOutMeterDetails,
            pathname: onboardingRoutesPaths.moveOutMeterDetails.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.EnergyLandscape,
            pathname: onboardingRoutesPaths.energyLandscape.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.ElectricityMeterIneligible,
            pathname: onboardingRoutesPaths.electricityMeterIneligible.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.GasMeterIneligible,
            pathname: onboardingRoutesPaths.gasMeterIneligible.resolve(),
            query,
        },
        {
            name: OnboardingRouteNames.AddressIneligible,
            pathname: onboardingRoutesPaths.addressIneligible.resolve(),
            query,
        },
        ...commonSteps,
    ] as const satisfies WizardSteps<OnboardingRouteNames>
}
